import 'react-datepicker/dist/react-datepicker.css';
import attendanceService from 'ApiService/Teacher/Attendance';
import Card from 'components/card';
import Nodatafound from 'pages/Common/Nodatafound';
import { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import { MdSearch, MdDownload, MdUpload } from 'react-icons/md';
import { BallTriangle } from 'react-loader-spinner';
import { toLocalDateStr } from 'utils/date';

const BulkSheet = ({ classData, minDate, onSuccess }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateColumns, setDateColumns] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceGrid, setAttendanceGrid] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [csvUploading, setCsvUploading] = useState(false);
  const [touchedDates, setTouchedDates] = useState(new Set());
  const fileInputRef = useRef(null);

  const filteredStudents = students.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDate();
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleLoad = async () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both from and to dates.');
      return;
    }
    if (fromDate > toDate) {
      toast.error('From date must be before or equal to to-date.');
      return;
    }

    setLoading(true);
    try {
      const res = await attendanceService.getBulkAttendanceData(
        classData.courseId,
        classData.batchId,
        toLocalDateStr(fromDate),
        toLocalDateStr(toDate),
      );

      if (!res.data.success) {
        toast.error(res.data.message || 'Failed to load data.');
        return;
      }

      const { students: studentList, dates, attendanceMap } = res.data.data;

      setStudents(studentList || []);
      setDateColumns(dates || []);

      const grid = {};
      for (const student of studentList || []) {
        grid[student._id] = {};
        const rawEnrollmentDate = student.enrollmentDate
          ? new Date(student.enrollmentDate)
          : null;
        const enrollmentDate = rawEnrollmentDate
          ? new Date(
              rawEnrollmentDate.getFullYear(),
              rawEnrollmentDate.getMonth(),
              rawEnrollmentDate.getDate(),
            )
          : null;
        for (const dateStr of dates || []) {
          const d = new Date(dateStr + 'T00:00:00');
          const joinedAfterThisDate = enrollmentDate && enrollmentDate > d;
          const dayAttendance = attendanceMap[dateStr];

          // An explicit mark by the teacher always wins — including on days before
          // this student joined, which is every day opened up by back-dating the
          // batch. Pre-join days the teacher never marked stay '-'.
          if (dayAttendance?.unmarkedStudentIds?.includes(student._id)) {
            grid[student._id][dateStr] = null;
          } else if (dayAttendance?.absentStudentIds?.includes(student._id)) {
            grid[student._id][dateStr] = false;
          } else if (dayAttendance?.presentStudentIds?.includes(student._id)) {
            grid[student._id][dateStr] = true;
          } else if (joinedAfterThisDate) {
            // Before joining: attendance from the batch they came from, else '-'.
            grid[student._id][dateStr] =
              student.previousAttendance?.[dateStr] ?? null;
          } else if (dayAttendance) {
            // A saved day that records Present explicitly lists everyone who was
            // on the roster then, so a student missing from it was added to the
            // batch later and stays '-'. Older records only stored absentees,
            // so there an unlisted student is present.
            grid[student._id][dateStr] = dayAttendance.presentStudentIds?.length
              ? null
              : true;
          } else {
            grid[student._id][dateStr] = null;
          }
        }
      }
      setAttendanceGrid(grid);
      setTouchedDates(new Set());
      setLoaded(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to load attendance data.',
      );
    } finally {
      setLoading(false);
    }
  };

  const cycleValue = (current) => {
    if (current === null) return true;
    if (current === true) return false;
    return null;
  };

  const handleCheckboxChange = (studentId, dateStr) => {
    const current = attendanceGrid[studentId]?.[dateStr];
    const nextValue = cycleValue(current);
    setAttendanceGrid((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [dateStr]: nextValue,
      },
    }));
    setTouchedDates((prev) => new Set(prev).add(dateStr));
  };

  const getColumnState = (dateStr) => {
    const activeStudents = students.filter((s) => !s.isCompleted);
    if (activeStudents.length === 0) return 'mixed';
    const values = activeStudents.map(
      (s) => attendanceGrid[s._id]?.[dateStr],
    );
    if (values.every((v) => v === null)) return 'null';
    if (values.every((v) => v === true)) return 'true';
    if (values.every((v) => v === false)) return 'false';
    return 'mixed';
  };

  const handleSelectAll = (dateStr) => {
    const state = getColumnState(dateStr);
    let nextValue;
    if (state === 'null') nextValue = true;
    else if (state === 'true') nextValue = false;
    else if (state === 'false') nextValue = null;
    else nextValue = null;

    setAttendanceGrid((prev) => {
      const next = { ...prev };
      for (const student of students) {
        if (student.isCompleted) continue;
        next[student._id] = {
          ...(next[student._id] || {}),
          [dateStr]: nextValue,
        };
      }
      return next;
    });
    setTouchedDates((prev) => new Set(prev).add(dateStr));
  };

  const submitableDates = dateColumns.filter((dateStr) => {
    const activeStudents = students.filter((s) => !s.isCompleted);
    return (
      touchedDates.has(dateStr) ||
      activeStudents.some((s) => attendanceGrid[s._id]?.[dateStr] !== null)
    );
  });

  const handleSubmit = async () => {
    if (submitting) return;

    const absentData = submitableDates.map((dateStr) => ({
        date: dateStr,
        absentStudentIds: students
          .filter(
            (s) =>
              !s.isCompleted &&
              attendanceGrid[s._id]?.[dateStr] === false,
          )
          .map((s) => s._id),
        unmarkedStudentIds: students
          .filter(
            (s) =>
              !s.isCompleted &&
              attendanceGrid[s._id]?.[dateStr] === null,
          )
          .map((s) => s._id),
        // Sent explicitly so a Present mark is recorded rather than inferred — on
        // back-dated days an inferred Present cannot be told apart from "unmarked".
        presentStudentIds: students
          .filter(
            (s) =>
              !s.isCompleted &&
              attendanceGrid[s._id]?.[dateStr] === true,
          )
          .map((s) => s._id),
      }));

    setSubmitting(true);
    try {
      const res = await attendanceService.bulkSubmitAttendance({
        courseId: classData.courseId,
        batchId: classData.batchId,
        attendanceData: absentData,
      });

      if (res.data.success) {
        toast.success(
          res.data.message || 'Bulk attendance submitted successfully.',
        );
        setLoaded(false);
        setStudents([]);
        setDateColumns([]);
        setAttendanceGrid({});
        setTouchedDates(new Set());
        setFromDate(null);
        setToDate(null);
        if (onSuccess) onSuccess();
      } else {
        toast.error(res.data.message || 'Failed to submit bulk attendance.');
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Something went wrong.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const escapeCSV = (val) => {
    const str = String(val ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const generateCSVContent = () => {
    const header = ['Sr No', 'Institute ID', 'Student Name', ...dateColumns];
    const rows = students.map((student, idx) =>
      [
        idx + 1,
        escapeCSV(student.instituteId || ''),
        escapeCSV(student.name),
        ...dateColumns.map((dateStr) => {
          const val = attendanceGrid[student._id]?.[dateStr];
          if (val === null || val === undefined) return '-';
          return val ? 'P' : 'A';
        }),
      ].join(','),
    );
    return [header.join(','), ...rows].join('\n');
  };

  const downloadCSV = () => {
    const csv = generateCSVContent();
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `attendance_${classData.batchId || 'bulk'}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded successfully.');
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file.');
      e.target.value = '';
      return;
    }

    setCsvUploading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let text = event.target.result;
        if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) {
          toast.error('CSV file is empty or has no data rows.');
          return;
        }

        const headers = parseCSVLine(lines[0]);
        const instIdIdx = headers.findIndex(
          (h) =>
            /institute/i.test(h) && /id|no|number|roll/i.test(h),
        );
        const nameIdx = headers.findIndex((h) =>
          /student\s*name|name/i.test(h),
        );

        if (instIdIdx === -1 && nameIdx === -1) {
          toast.error(
            'CSV must have "Institute ID" or "Student Name" column.',
          );
          return;
        }

        const srNoIdx = headers.findIndex(
          (h) => /sr\s*no|sr\.?\s*#|s\.?no|#/i.test(h),
        );

        const lastIdCol = Math.max(
          instIdIdx !== -1 ? instIdIdx : -1,
          nameIdx !== -1 ? nameIdx : -1,
          srNoIdx !== -1 ? srNoIdx : -1,
        );
        const dateColStart = lastIdCol + 1;

        const lookup = {};
        const nameLookup = {};
        students.forEach((s, idx) => {
          const idKey = s.instituteId != null
            ? String(s.instituteId).toLowerCase().trim()
            : null;
          if (idKey) lookup[idKey] = s;
          const nameKey = s.name.toLowerCase().trim();
          lookup[nameKey] = s;
          nameLookup[nameKey] = s;
          lookup[`_idx_${idx}`] = s;
        });

        let matched = 0;
        const updatedEntries = {};

        for (let r = 1; r < lines.length; r++) {
          const fields = parseCSVLine(lines[r]);

          let student = null;

          if (instIdIdx !== -1) {
            const rawId = fields[instIdIdx];
            if (rawId) {
              student = lookup[String(rawId).trim().toLowerCase()];
            }
          }

          if (!student && nameIdx !== -1) {
            const rawName = fields[nameIdx];
            if (rawName) {
              student = lookup[String(rawName).trim().toLowerCase()];
              if (!student && nameLookup) {
                const csvName = String(rawName).trim().toLowerCase();
                const fuzzy = Object.keys(nameLookup).find(
                  (k) => k.replace(/\s+/g, ' ') === csvName.replace(/\s+/g, ' '),
                );
                if (fuzzy) student = nameLookup[fuzzy];
              }
            }
          }

          if (!student && srNoIdx !== -1) {
            const rawSr = fields[srNoIdx];
            if (rawSr) {
              const idx = parseInt(String(rawSr).trim(), 10) - 1;
              if (idx >= 0 && idx < students.length) {
                student = students[idx];
              }
            }
          }

          if (!student) continue;

          matched++;
          const studentId = student._id;
          if (!updatedEntries[studentId]) updatedEntries[studentId] = {};

          for (let d = 0; d < dateColumns.length; d++) {
            const dateStr = dateColumns[d];
            const colIdx = dateColStart + d;
            const val = fields[colIdx]?.trim().toUpperCase();
            if (val === 'P') {
              updatedEntries[studentId][dateStr] = true;
            } else if (val === 'A') {
              updatedEntries[studentId][dateStr] = false;
            } else {
              updatedEntries[studentId][dateStr] = null;
            }
          }
        }

        if (matched > 0) {
          setAttendanceGrid((prev) => {
            const next = {};
            for (const key of Object.keys(prev)) {
              next[key] = { ...prev[key] };
            }
            for (const studentId of Object.keys(updatedEntries)) {
              next[studentId] = {
                ...(next[studentId] || {}),
                ...updatedEntries[studentId],
              };
            }
            return next;
          });
          setTouchedDates((prev) => {
            const next = new Set(prev);
            dateColumns.forEach((d) => next.add(d));
            return next;
          });
        }
        toast.success(
          `${matched} student(s) updated from CSV.` +
            (matched < lines.length - 1
              ? ` (${lines.length - 1 - matched} unmatched row(s) skipped)`
              : ''),
        );
      } catch (err) {
        toast.error('Failed to parse CSV file.');
      } finally {
        setCsvUploading(false);
        e.target.value = '';
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read file.');
      setCsvUploading(false);
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  return (
    <Card extra={'w-full sm:overflow-auto p-4 '}>
      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-start">
        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium">From Date:</label>
          <DatePicker
            selected={fromDate}
            onChange={(d) => setFromDate(d)}
            className="rounded border px-2 py-1 dark:bg-navy-800"
            minDate={minDate}
            maxDate={new Date()}
            placeholderText="Select from date"
            withPortal
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium">To Date:</label>
          <DatePicker
            selected={toDate}
            onChange={(d) => setToDate(d)}
            className="rounded border px-2 py-1 dark:bg-navy-800"
            minDate={minDate || fromDate}
            maxDate={new Date()}
            placeholderText="Select to date"
            withPortal
          />
        </div>
        <button
          onClick={handleLoad}
          disabled={loading}
          className="rounded-lg bg-brand-900 px-6 py-2 font-medium text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Load'}
        </button>
      </div>

      {loaded && (
        <div className="mb-4 flex items-center">
          <div className="relative max-w-xs">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
            <MdSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
      )}

      {loading && (
        <div className="flex h-64 items-center justify-center">
          <BallTriangle color="#5431ed" />
        </div>
      )}

      {loaded && !loading && (
        <>
          <div className="mb-4 flex flex-wrap gap-3">
            <button
              onClick={downloadCSV}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <MdDownload size={18} />
              Download CSV
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={csvUploading}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              <MdUpload size={18} />
              {csvUploading ? 'Uploading...' : 'Upload CSV'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
            />
          </div>
          <div className="mt-4 overflow-x-auto">
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <span className="inline-block h-4 w-4 rounded bg-green-100 text-center font-bold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                P
              </span>
              Present
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-4 w-4 rounded bg-red-100 text-center font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                A
              </span>
              Absent
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-4 w-4 rounded border border-gray-300 text-center font-bold text-gray-400">
                -
              </span>
              Not marked
            </span>
          </div>
          {filteredStudents.length > 0 ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="sticky left-0 z-10 bg-white px-3 py-3 text-center text-xs font-bold uppercase tracking-wide text-gray-600 dark:bg-navy-900">
                    SR#
                  </th>
                  <th className="sticky left-12 z-10 min-w-[200px] bg-white px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:bg-navy-900">
                    NAME
                  </th>
                  {dateColumns.map((dateStr) => {
                    const colState = getColumnState(dateStr);
                    const allLabel =
                      colState === 'mixed' || colState === 'null'
                        ? '-'
                        : colState === 'true'
                          ? 'P'
                          : 'A';
                    return (
                      <th
                        key={dateStr}
                        className="min-w-[130px] px-2 py-3 text-center text-xs font-bold uppercase tracking-wide text-gray-600"
                      >
                        <div>{formatDateLabel(dateStr)}</div>
                        <div className="mt-1 flex items-center justify-center">
                          <button
                            onClick={() => handleSelectAll(dateStr)}
                            title="Toggle all students (-/P/A)"
                            className={`flex h-6 w-6 items-center justify-center rounded border text-[10px] font-bold transition ${
                              colState === 'true'
                                ? 'border-green-500 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : colState === 'false'
                                  ? 'border-red-500 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  : 'border-gray-300 text-gray-400 hover:bg-gray-100 dark:border-navy-500 dark:hover:bg-navy-700'
                            }`}
                          >
                            {allLabel}
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, idx) => (
                  <tr
                    key={student._id}
                    className={`border-b dark:border-white/10 ${
                      student.isCompleted
                        ? 'bg-gray-50 dark:bg-gray-800/50'
                        : 'border-gray-200'
                    }`}
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-3 text-center text-sm font-medium text-navy-700 dark:bg-navy-900 dark:text-white">
                      {idx + 1}
                    </td>
                    <td className="sticky left-12 z-10 bg-white px-3 py-3 text-sm font-medium text-navy-700 dark:bg-navy-900 dark:text-white">
                      <span
                        className={
                          student.isCompleted
                            ? 'text-gray-400 line-through'
                            : ''
                        }
                      >
                        {student.name}
                      </span>
                      {student.addedOn && (
                        <div className="text-[10px] font-normal text-gray-500">
                          Added:{' '}
                          {new Date(student.addedOn).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                      )}
                      {student.isCompleted && (
                        <span className="ml-2 text-[10px] text-gray-500">
                          (Completed)
                        </span>
                      )}
                    </td>
                    {dateColumns.map((dateStr) => {
                      const val = attendanceGrid[student._id]?.[dateStr];
                      return (
                        <td key={dateStr} className="px-2 py-3 text-center">
                          <button
                            onClick={() => {
                              if (!student.isCompleted) {
                                handleCheckboxChange(
                                  student._id,
                                  dateStr,
                                );
                              }
                            }}
                            disabled={student.isCompleted}
                            className={`h-7 w-7 rounded text-sm font-bold transition ${
                              val === null
                                ? 'text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700'
                                : val
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                            }`}
                          >
                            {val === null
                              ? '-'
                              : val
                                ? 'P'
                                : 'A'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center py-10">
              <Nodatafound
                title={
                  searchTerm
                    ? `No students matching "${searchTerm}"`
                    : 'No students found.'
                }
              />
            </div>
          )}
        </div>
          {filteredStudents.length > 0 && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-lg bg-green-600 px-8 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {submitting
                  ? 'Submitting...'
                  : `Submit Bulk Attendance (${submitableDates.length} dates)`}
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default BulkSheet;
